import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AssignVirtualNumbers,
  OrganisationsService,
  PatchedSaveSmsAgentRequest,
  PromptCreateOrUpdate,
  SmsFlowCreateRequest,
  SmsTemplatesService,
  UnarchiveFlowRequest,
} from 'Service'
import { message, notification } from 'antd'

const useGetSmsFlows = ({
  organisationUuid,
  templateType,
}: {
  organisationUuid: string
  templateType: string
}) => {
  return useQuery({
    queryKey: ['list-sms-agent', templateType, organisationUuid],
    queryFn: () =>
      OrganisationsService.organisationsSmsFlowsList({
        organisationUuid,
        templateType,
      }),
    enabled: !!organisationUuid,
  })
}

const useGetSmsTemplates = ({ status, templateType }: { status: string; templateType: string }) => {
  return useQuery({
    queryKey: ['list-template', templateType, status],
    queryFn: () => SmsTemplatesService.smsTemplatesList({ templateType, status }),
    enabled: !!templateType && !!status,
  })
}

const useCreateSmsTemplate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      organisationUuid,
      requestBody,
    }: {
      organisationUuid: string
      requestBody: SmsFlowCreateRequest
    }) =>
      OrganisationsService.organisationsSmsFlowsCreate({
        organisationUuid,
        requestBody,
      }),
    onSuccess: () => {
      message.success('Sms template created')
      queryClient.invalidateQueries({ queryKey: ['list-sms-template'] })
    },
    onError() {
      notification.error({
        message: 'Error ocurred while creating sms template',
      })
    },
  })
}

const useGetOrganisationList = () => {
  return useQuery({
    queryKey: ['studio', 'organisations', 'list'],
    queryFn: () => OrganisationsService.organisationsList({ fields: ['name', 'uuid'] }),
  })
}

const useUpdateSmsAgent = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      organisationUuid,
      uuid,
      requestBody,
    }: {
      organisationUuid: string
      uuid: string
      requestBody: PatchedSaveSmsAgentRequest
    }) =>
      OrganisationsService.organisationsSmsFlowsSaveSmsAgentPartialUpdate({
        organisationUuid,
        uuid,
        requestBody,
      }),
    onSuccess: (_, { organisationUuid }) => {
      queryClient.invalidateQueries({ queryKey: ['sms-agent', organisationUuid] })
      message.success('Updated agent configurations')
    },
    onError() {
      notification.error({
        message: 'Error ocurred while updating sms template',
      })
    },
  })
}

const useUpdateAssignVirtualNumber = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      organisationUuid,
      uuid,
      requestBody,
    }: {
      organisationUuid: string
      uuid: string
      requestBody: AssignVirtualNumbers
    }) =>
      OrganisationsService.organisationsSmsFlowsAssignVirtualNumbersCreate({
        organisationUuid,
        uuid,
        requestBody,
      }),
    onSuccess: (_, { organisationUuid }) => {
      queryClient.invalidateQueries({ queryKey: ['sms-agent', organisationUuid] })
      message.success('Assigned numbers updated for agent')
    },
    onError() {
      notification.error({
        message: 'Error ocurred while updating agent config',
      })
    },
  })
}

const useArchieveSmsAgent = (type: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ organisationUuid, uuid }: { organisationUuid: string; uuid: string }) =>
      OrganisationsService.organisationsFlowsArchiveUpdate({
        organisationUuid,
        uuid,
      }),
    onSuccess: (_, { organisationUuid }) => {
      queryClient.invalidateQueries({ queryKey: ['sms-agent', organisationUuid] })
    },
    onError() {
      notification.error({
        message: 'Error ocurred while updating template',
      })
    },
  })
}

const useUnarchieveSmsAgent = (type: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      organisationUuid,
      uuid,
      requestBody,
    }: {
      organisationUuid: string
      uuid: string
      requestBody: UnarchiveFlowRequest
    }) =>
      OrganisationsService.organisationsFlowsUnarchiveUpdate({
        organisationUuid,
        uuid,
        requestBody,
      }),
    onSuccess: (_, { organisationUuid }) => {
      queryClient.invalidateQueries({ queryKey: ['sms-agent', organisationUuid] })
    },
    onError() {
      notification.error({
        message: 'Error ocurred while updating template',
      })
    },
  })
}

const useGetFlow = ({
  organisationUuid,
  flow_uuid,
  ...props
}: {
  organisationUuid: string
  flow_uuid: string
}) => {
  return useQuery({
    queryKey: ['sms-flow', flow_uuid],
    queryFn: () =>
      OrganisationsService.organisationsSmsFlowsRetrieve({
        organisationUuid,
        uuid: flow_uuid,
        ...props,
      }),
    enabled: !!organisationUuid && !!flow_uuid,
  })
}

const useGetSmsFlowPrompts = ({
  organisationUuid,
  uuid,
}: {
  organisationUuid: string
  uuid: string
}) => {
  return useQuery({
    queryKey: ['sms-prompts', organisationUuid],
    queryFn: () =>
      OrganisationsService.organisationsSmsFlowsGetScriptPromptsRetrieve({
        organisationUuid,
        uuid,
      }),
    enabled: !!organisationUuid && !!uuid,
  })
}

const useUpdateSmsPrompts = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      organisationUuid,
      uuid,
      requestBody,
    }: {
      organisationUuid: string
      uuid: string
      requestBody: PromptCreateOrUpdate
    }) =>
      OrganisationsService.organisationsSmsFlowsCreateOrUpdatePromptCreate({
        organisationUuid,
        uuid,
        requestBody,
      }),
    onSuccess: (_, { organisationUuid }) => {
      message.success('Prompts updated')
      queryClient.invalidateQueries({ queryKey: ['sms-prompts', organisationUuid] })
    },
    onError(error) {
      message.error(error?.message)
    },
  })
}

export {
  useArchieveSmsAgent,
  useCreateSmsTemplate,
  useGetFlow,
  useGetSmsFlowPrompts,
  useGetSmsTemplates,
  useUnarchieveSmsAgent,
  useUpdateSmsAgent,
  useUpdateSmsPrompts,
  useUpdateAssignVirtualNumber,
}
